import axios, { type AxiosResponse, AxiosError } from "axios";
import { attachRateLimitRetry } from "./withRateLimitRetry";

const API_BASE_URL = import.meta.env.VITE_BACKEND_URL;

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Retries rate-limited GETs with backoff, honouring Retry-After.
attachRateLimitRetry(apiClient);

// Match backend filters exactly
export interface TestStatsFilters {
  dateFrom?: string;
  dateTo?: string;
  testType?: string;
  difficulty?: string;
  status?: string;
  attemptNumber?: number;
  passed?: boolean;
  percentageMin?: number;
  percentageMax?: number;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export interface TestStatsResponse {
  success: boolean;
  message: string;
  data: any; // Replace with your actual type
  timestamp: string;
}

export interface ApiError {
  message: string;
  statusCode?: number;
  error?: string;
}

export const getTestStats = async (
  filters: TestStatsFilters = {}
): Promise<TestStatsResponse> => {
  try {
    // Remove undefined/empty values
    const cleanFilters = Object.entries(filters).reduce(
      (acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, any>
    );

    const response: AxiosResponse<TestStatsResponse> = await apiClient.get(
      "/api/testing/stats",
      {
        params: cleanFilters,
      }
    );

    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw {
        message:
          error.response?.data?.message ||
          error.message ||
          "An error occurred",
        statusCode: error.response?.status,
        error: error.response?.data?.error,
      } as ApiError;
    }
    throw {
      message: "An unexpected error occurred",
      statusCode: 500,
    } as ApiError;
  }
};

// Utility function aligned with backend filters
export const getTestStatsWithCommonFilters = async (options: {
  dateRange?: { from: string; to: string };
  testType?: string;
  difficulty?: string;
  status?: string;
  attemptNumber?: number;
  passed?: boolean;
  percentageMin?: number;
  percentageMax?: number;
  searchTerm?: string;
  pagination?: { page: number; limit: number };
  sorting?: { sortBy: string; sortOrder: "asc" | "desc" };
}): Promise<TestStatsResponse> => {
  const filters: TestStatsFilters = {
    ...(options.dateRange && {
      dateFrom: options.dateRange.from,
      dateTo: options.dateRange.to,
    }),
    ...(options.testType && { testType: options.testType }),
    ...(options.difficulty && { difficulty: options.difficulty }),
    ...(options.status && { status: options.status }),
    ...(options.attemptNumber !== undefined && {
      attemptNumber: options.attemptNumber,
    }),
    ...(options.passed !== undefined && { passed: options.passed }),
    ...(options.percentageMin !== undefined && {
      percentageMin: options.percentageMin,
    }),
    ...(options.percentageMax !== undefined && {
      percentageMax: options.percentageMax,
    }),
    ...(options.searchTerm && { search: options.searchTerm }),
    ...(options.pagination && {
      page: options.pagination.page,
      limit: options.pagination.limit,
    }),
    ...(options.sorting && {
      sortBy: options.sorting.sortBy,
      sortOrder: options.sorting.sortOrder,
    }),
  };

  return getTestStats(filters);
};

export default {
  getTestStats,
  getTestStatsWithCommonFilters,
};
